import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Loader2, ExternalLink, ShieldCheck, ShieldAlert, ShieldQuestion, Sparkles } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import { Link } from "react-router-dom";

const VERDICT_META = {
    "true":  { label: "TRUE",  icon: ShieldCheck,    color: "#22e0b4", bg: "rgba(34,224,180,0.14)",   border: "rgba(34,224,180,0.45)" },
    "false": { label: "FALSE", icon: ShieldAlert,    color: "#ff6b6b", bg: "rgba(255,107,107,0.14)",  border: "rgba(255,107,107,0.45)" },
    "mixed": { label: "MIXED", icon: ShieldQuestion, color: "#f5b942", bg: "rgba(245,185,66,0.14)",   border: "rgba(245,185,66,0.45)" },
};

const SUGGESTIONS = [
    "Did NASA cancel the Artemis moon mission?",
    "Is 5G radiation dangerous?",
    "Do sharks get cancer?",
    "Is oatmeal actually good for cholesterol?",
];

function safeHost(url) {
    try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}

/**
 * Search the Truth — hero-adjacent live search bar backed by the Reality-Check engine.
 */
export default function SearchTheTruth() {
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    async function run(question) {
        const trimmed = (question || "").trim();
        if (trimmed.length < 3) return toast.error("Ask a real question");
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.post("/reality-check", { question: trimmed });
            setResult(data);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || "Engine unavailable — try again.";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }

    const meta = result && (VERDICT_META[result.verdict] || VERDICT_META.mixed);
    const VerdictIcon = meta?.icon;

    return (
        <section className="max-w-4xl mx-auto px-6 lg:px-10 py-14" data-testid="search-truth">
            <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/15 px-3 py-1 text-[11px] font-semibold text-accent mb-4">
                    <Sparkles className="h-3 w-3 text-primary" /> Real-Check Reality Engine · Live web
                </div>
                <h2 className="font-display text-3xl md:text-5xl text-accent tracking-tight leading-[1.05]">
                    Search the Truth.
                </h2>
                <p className="mt-3 text-lg text-muted-foreground max-w-2xl mx-auto">
                    Ask anything happening in the world right now. Our engine pulls live data, weighs the evidence, and delivers a verdict — with the top 3 verified sources.
                </p>
            </div>

            {/* Search bar */}
            <form
                onSubmit={(e) => { e.preventDefault(); run(q); }}
                className="relative"
                data-testid="search-truth-form"
            >
                <div className="relative flex items-center gap-2 bg-white/[0.04] backdrop-blur-md border border-primary/30 rounded-full pl-6 pr-2 py-2 shadow-2xl shadow-primary/10 focus-within:border-primary/70 transition-colors">
                    <Search className="h-5 w-5 text-primary shrink-0" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Ask anything — is it real?"
                        maxLength={500}
                        autoComplete="off"
                        className="flex-1 bg-transparent border-none outline-none text-base md:text-lg text-accent placeholder:text-muted-foreground/60 py-3"
                        data-testid="search-truth-input"
                    />
                    <Button
                        type="submit"
                        disabled={loading || !q.trim()}
                        className="rounded-full h-11 px-5 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/30 shrink-0"
                        data-testid="search-truth-submit"
                    >
                        {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Checking…</> : "Reality Check"}
                    </Button>
                </div>
            </form>

            {/* Suggestion chips */}
            {!result && !loading && (
                <div className="mt-4 flex flex-wrap justify-center gap-2" data-testid="search-truth-suggestions">
                    {SUGGESTIONS.map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => { setQ(s); run(s); }}
                            className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-muted-foreground hover:border-primary/50 hover:text-accent transition-colors"
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}

            {/* Loading state — subtle skeleton */}
            {loading && (
                <div className="mt-8 text-center text-muted-foreground text-sm" data-testid="search-truth-loading">
                    <Loader2 className="h-6 w-6 mx-auto animate-spin text-primary mb-3" />
                    Consulting live sources across the web…
                </div>
            )}

            {/* Verdict card */}
            <AnimatePresence>
                {result && meta && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="mt-8"
                        data-testid="search-truth-result"
                    >
                        <Card
                            className="rounded-2xl p-6 md:p-8 relative overflow-hidden"
                            style={{ background: meta.bg, borderColor: meta.border, borderWidth: 1 }}
                        >
                            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full blur-3xl pointer-events-none" style={{ background: meta.color, opacity: 0.15 }} />
                            <div className="relative">
                                {/* Verdict pill */}
                                <div className="flex flex-wrap items-center gap-3 mb-5">
                                    <div
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold tracking-widest text-sm"
                                        style={{ background: meta.color, color: "#041a1e" }}
                                        data-testid={`search-truth-verdict-${result.verdict}`}
                                    >
                                        {VerdictIcon && <VerdictIcon className="h-4 w-4" />}
                                        {meta.label}
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                        <span className="font-mono text-accent tabular-nums">{result.confidence}%</span> confidence
                                    </div>
                                </div>

                                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">You asked</p>
                                <p className="text-accent italic mb-5">&ldquo;{result.question}&rdquo;</p>

                                <p className="font-display text-xl md:text-2xl text-accent leading-snug mb-4" data-testid="search-truth-oneliner">
                                    {result.one_line_answer}
                                </p>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-6 whitespace-pre-line" data-testid="search-truth-explanation">
                                    {result.explanation}
                                </p>

                                {/* Sources */}
                                {result.sources?.length > 0 && (
                                    <div data-testid="search-truth-sources">
                                        <p className="text-xs uppercase tracking-widest text-primary mb-3 flex items-center gap-1.5">
                                            <ShieldCheck className="h-3 w-3" /> Top verified sources
                                        </p>
                                        <div className="grid gap-2">
                                            {result.sources.slice(0, 3).map((s, i) => (
                                                <a
                                                    key={s.url}
                                                    href={s.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 hover:border-primary/40 hover:bg-white/[0.06] transition-colors group"
                                                    data-testid={`search-truth-source-${i}`}
                                                >
                                                    <img
                                                        src={`https://www.google.com/s2/favicons?domain=${safeHost(s.url)}&sz=32`}
                                                        alt=""
                                                        className="w-5 h-5 rounded"
                                                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm text-accent truncate group-hover:text-primary transition-colors">{s.title || s.url}</div>
                                                        <div className="text-[11px] text-muted-foreground font-mono truncate">{safeHost(s.url)}</div>
                                                    </div>
                                                    <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary shrink-0" />
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="mt-6 flex flex-wrap gap-3">
                                    <Link to={`/check/${result.check_id}`}>
                                        <Button variant="secondary" className="rounded-full" data-testid="search-truth-permalink">
                                            Open full result →
                                        </Button>
                                    </Link>
                                    <Button
                                        variant="ghost"
                                        className="rounded-full"
                                        onClick={() => { setResult(null); setQ(""); }}
                                        data-testid="search-truth-reset"
                                    >
                                        Ask something else
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
